// @flow

import React, {Fragment} from 'react';
import gql from 'graphql-tag';
import {FormattedMessage} from 'react-intl';
import {get} from 'lodash';
import PriceSummary from '@homeaway/react-price-summary';
import InstantBookIcon from '../InstantBookIcon/InstantBookIcon';
import {type IntlShape} from 'react-intl';
import {type ListingShape} from '../Hit/Hit';
import './Price.less';

import type {Node} from 'react';

export const fragments = {
    listing: gql`
        fragment PriceListing on Listing {
            priceSummary: priceSummary {
                priceTypeId
                edapEventJson
                formattedAmount
                pricePeriodDescription
                priceAccurate
            }
            priceSummarySecondary: priceSummary(
                summary: "displayPriceSecondary"
            ) {
                priceTypeId
                edapEventJson
                formattedAmount
                pricePeriodDescription
            }
            priceLabel: priceSummary(summary: "priceLabel") {
                priceTypeId
                pricePeriodDescription
            }
        }
    `
};

export const listingHasPrice = (
    listing: ListingShape,
    isPriceAccurateAllowed?: boolean,
    isDatelessSearch?: boolean
): boolean => {
    const {priceSummary} = listing || {};
    const isPriceAccurate = get(priceSummary, 'priceAccurate', true);
    return isPriceAccurateAllowed
        ? !!priceSummary && isPriceAccurate && !isDatelessSearch
        : !!priceSummary;
};

export const listingHasPriceSecondary = (
    listing: ListingShape,
    isPriceAccurateAllowed: boolean
): boolean => {
    const {priceSummarySecondary} = listing || {};
    const isPriceAccurate = get(listing, 'priceSummary.priceAccurate', true);
    return isPriceAccurateAllowed
        ? !!priceSummarySecondary && isPriceAccurate
        : !!priceSummarySecondary;
};

export type PriceSummaryShape = {
    priceTypeId: string,
    edapEventJson: string,
    formattedAmount: string,
    pricePeriodDescription: string
};

export type PriceListingShape = {
    priceSummary: ?PriceSummaryShape,
    priceSummarySecondary: ?PriceSummaryShape,
    priceLabel: ?PriceSummaryShape
};

export type Props = {
    listing: ListingShape,
    isPriceAccurateAllowed?: boolean,
    isDatelessSearch?: boolean
};

type WithInstantBookProps = {
    intl: IntlShape,
    instantBookable?: boolean
} & Props;

export function Price({
    listing,
    isPriceAccurateAllowed,
    isDatelessSearch
}: Props) {
    if (listingHasPrice(listing, isPriceAccurateAllowed, isDatelessSearch)) {
        return <PriceSummary priceSummary={listing.priceSummary} />;
    }

    return (
        <span className="Price Price--noPrice" data-wdio="Price" data-price="0">
            <FormattedMessage
                id={'ratesNone'}
                description={'no price'}
                defaultMessage={'View details for price'}
            >
                {(txt: Node): Node => (
                    <span className="Price__Fallback">{txt}</span>
                )}
            </FormattedMessage>
        </span>
    );
}

export default function PriceWithInstantBook(props: WithInstantBookProps) {
    return (
        <Fragment>
            {props.instantBookable && <InstantBookIcon intl={props.intl} />}
            <Price {...props} />
        </Fragment>
    );
}
